import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { BadgeCheck, MapPin, Users, MessageCircle, Clock, CalendarClock } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { ProductCard } from '../components/commerce/ProductCard';
import { RatingStars } from '../components/commerce/RatingStars';
import { toast } from 'sonner';
import { fileUrl } from '../lib/upload';
import { waLink } from '../lib/wa';
import { useOpenStatus } from '../hooks/useOpenStatus';
const ALL_TABS = ['Home', 'Products', 'Promotions', 'Reviews', 'About'];
const LIST_TABS = ['About', 'Reviews'];

export default function StorePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { follows, toggleFollow, merchantProfiles, customMerchants, productsByMerchant } = useApp();
  const { user } = useAuth();
  const merchant = customMerchants.find((m) => m.slug === slug);
  const isList = merchant?.plan === 'LIST';
  const tabs = isList ? LIST_TABS : ALL_TABS;
  const [tab, setTab] = useState(isList ? 'About' : 'Home');
  const openStatus = useOpenStatus(merchant?.id);
  const mtype = (merchant?.merchantType || 'PRODUCT').toUpperCase();
  const isService = mtype === 'SERVICE';
  const closed = openStatus && !openStatus.open;
  if (!merchant) return <div className="mx-auto max-w-[1280px] px-4 py-20 text-center text-kelate-soft">Store not found.</div>;
  const products = productsByMerchant(merchant.id);
  const isFollowing = follows.includes(merchant.id);
  const cats = merchantProfiles[merchant.id]?.categories || merchant.categories || [merchant.category];

  const follow = () => {
    if (!user) { toast.info('Log in to follow stores'); navigate('/account/login'); return; }
    toggleFollow(merchant.id);
  };

  return (
    <div data-testid="store-page">
      <div className="relative h-40 md:h-56 bg-[#EFEAE2]">
        {merchant.banner && <img src={fileUrl(merchant.banner)} alt="" className="h-full w-full object-cover" />}
      </div>
      <div className="mx-auto max-w-[1280px] px-4">
        <div className="mt-4 flex flex-wrap items-center justify-between gap-4 rounded-3xl bg-kelate-surface p-4 shadow-[0_1px_4px_rgba(81,68,55,0.1)]">
          <div className="flex items-end gap-4">
            {merchant.logo ? (
              <img src={fileUrl(merchant.logo)} alt={merchant.name} className="aspect-square h-20 w-20 rounded-2xl border border-kelate-line bg-white object-contain" />
            ) : (
              <span className="flex aspect-square h-20 w-20 items-center justify-center rounded-2xl bg-kelate-darkwood font-display text-2xl font-semibold text-kelate-surface">{merchant.name[0]}</span>
            )}
            <div className="pb-1">
              <div className="flex items-center gap-2">
                <h1 className="font-display text-xl font-semibold text-kelate-darkwood sm:text-2xl" data-testid="store-name">{merchant.name}</h1>
                {merchant.verified && <BadgeCheck size={18} className="text-kelate-sage" />}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-kelate-soft">
                <RatingStars rating={merchant.rating} />
                <span className="flex items-center gap-1"><Users size={11} /> {merchant.followers} followers</span>
                <span className="flex items-center gap-1"><MapPin size={11} /> {merchant.district}</span>
              </div>
            </div>
          </div>
          <div className="flex gap-2 pb-1">
            <button data-testid="store-follow-btn" onClick={follow} className={`rounded-sm border px-4 py-2 text-sm font-medium ${isFollowing ? 'border-kelate-sage text-[#5F7050]' : 'border-kelate-darkwood text-kelate-darkwood hover:bg-secondary'}`}>
              {isFollowing ? 'Following' : 'Follow'}
            </button>
            {merchant.mapsUrl && (
              <a data-testid="store-maps-btn" href={merchant.mapsUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 rounded-sm border border-kelate-darkwood px-4 py-2 text-sm font-medium text-kelate-darkwood hover:bg-secondary">
                <MapPin size={15} /> Visit Store
              </a>
            )}
            {isService && openStatus?.open !== false && (() => {
              const wa = waLink(merchant.whatsapp || merchant.phone, `Hi ${merchant.name}, I'd like to make an appointment through the KELATE website. Could you please share your available time slots? Thank you.`);
              return wa ? (
                <a data-testid="store-appointment-btn" href={wa} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 rounded-sm bg-kelate-sage px-4 py-2 text-sm font-medium text-white hover:opacity-90">
                  <CalendarClock size={15} /> Make Appointment
                </a>
              ) : null;
            })()}
            {(() => {
              const wa = waLink(merchant.whatsapp || merchant.phone, `Hi ${merchant.name}, I found your store on KELATE and would like to ask about your products.`);
              return wa ? (
                <a data-testid="store-whatsapp-btn" href={wa} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 rounded-sm bg-kelate-darkwood px-4 py-2 text-sm font-medium text-kelate-surface hover:bg-kelate-wood">
                  <MessageCircle size={15} /> WhatsApp
                </a>
              ) : null;
            })()}
          </div>
        </div>

        <div className="mt-4 flex gap-1 overflow-x-auto border-b border-kelate-line" data-testid="store-tabs">
          {tabs.map((t) => (
            <button
              key={t}
              data-testid={`store-tab-${t.toLowerCase()}`}
              onClick={() => setTab(t)}
              className={`whitespace-nowrap px-4 py-2.5 text-sm ${tab === t ? 'border-b-2 border-kelate-darkwood font-semibold text-kelate-darkwood' : 'text-kelate-soft hover:text-kelate-ink'}`}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="py-6">
          {closed && mtype === 'FNB' && (
            <div data-testid="store-closed-banner" className="mb-4 rounded-xl bg-kelate-red/10 px-4 py-3 text-sm text-kelate-red">
              This shop is closed now{openStatus?.opensAt ? ` (opens ${openStatus.opensAt}${openStatus?.closesAt ? ` – ${openStatus.closesAt}` : ''})` : ''}. You can browse but ordering is only available during operating hours.
            </div>
          )}
          {closed && mtype === 'PRODUCT' && (
            <div data-testid="store-nextday-banner" className="mb-4 rounded-xl bg-kelate-wood/10 px-4 py-3 text-sm text-kelate-wood">
              This shop is closed today. Orders placed now will be delivered the next day.
            </div>
          )}
          {isService && closed && (
            <div data-testid="store-service-closed-banner" className="mb-4 rounded-xl bg-kelate-wood/10 px-4 py-3 text-sm text-kelate-wood">
              This shop is closed today. Appointments can be made during operating hours.
            </div>
          )}
          {(tab === 'Home' || tab === 'Products') && (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
              {(tab === 'Home' ? products.slice(0, 4) : products).map((p) => <ProductCard key={p.id} product={p} />)}
              {products.length === 0 && <p className="col-span-full py-8 text-center text-sm text-kelate-soft">No products yet.</p>}
            </div>
          )}
          {tab === 'Promotions' && (
            <div className="rounded-md border border-dashed border-kelate-line bg-kelate-surface p-8 text-center text-sm text-kelate-soft" data-testid="store-promotions-empty">
              {['BOOST', 'BRAND'].includes(merchant.plan) ? 'No active promotions right now. Follow this store to get updates.' : 'Promotions are available on the BOOST plan and above.'}
            </div>
          )}
          {tab === 'Reviews' && (
            <div className="space-y-3" data-testid="store-reviews">
              {products.flatMap((p) => (p.reviews || []).map((r, i) => (
                <div key={`${p.id}-${i}`} className="rounded-md border border-kelate-line bg-kelate-surface p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-kelate-ink">{r.user} <span className="text-xs font-normal text-kelate-soft">on {p.name}</span></span>
                    <RatingStars rating={r.rating} />
                  </div>
                  <p className="mt-1 text-sm text-kelate-soft">{r.text}</p>
                </div>
              )))}
              {products.every((p) => !(p.reviews || []).length) && <p className="py-8 text-center text-sm text-kelate-soft">No reviews yet.</p>}
            </div>
          )}
          {tab === 'About' && (
            <div className="max-w-2xl rounded-2xl bg-kelate-surface p-5 shadow-[0_1px_4px_rgba(81,68,55,0.08)]" data-testid="store-about">
              <p className="text-sm leading-relaxed text-kelate-soft">{merchantProfiles[merchant.id]?.description || merchant.description}</p>
              <div className="mt-3 flex flex-wrap gap-1.5" data-testid="store-categories">
                {cats.map((c) => (
                  <span key={c} className="rounded-full bg-secondary px-2.5 py-1 text-[11px] font-medium text-kelate-ink">{c}</span>
                ))}
              </div>
              {merchant.plan === 'LIST' && (
                <div className="mt-4 rounded-2xl bg-secondary/60 px-4 py-3 text-xs text-kelate-soft" data-testid="list-plan-note">
                  This business is on the LIST plan — products and promotions are shown after upgrading to STORE (RM50/month). Reviews and business info remain public.
                </div>
              )}
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex items-center gap-2 text-kelate-ink">
                  <MapPin size={14} className="text-kelate-wood" /> {merchant.address}
                  {merchant.mapsUrl && (
                    <a data-testid="store-about-maps-link" href={merchant.mapsUrl} target="_blank" rel="noreferrer" className="text-xs font-medium text-kelate-wood underline underline-offset-2 hover:text-kelate-darkwood">
                      Open in Google Maps
                    </a>
                  )}
                </div>
                <div className="flex items-center gap-2 text-kelate-ink"><Clock size={14} className="text-kelate-wood" /> {merchant.hours}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
